import { useMemo } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import { Input, InputWithValidation, Radio, RadioGroup, Select, StyledText } from '@skytech/ui'
import { isOnlyLetters } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon'
import OfficeBuildingOutlineIcon from 'mdi-react/OfficeBuildingOutlineIcon'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useCart } from '@/contexts/cart/cart-context'
import { ecomPolicyResources } from '@/resources/ecom-policy'

import { useTranslation } from '../i18n/hooks'
import { Translation } from '../i18n/Translation'
import { radioBtnStyleByWhiteLabel } from '../util/style-constant'

const CUSTOM_ADDRESS_ID = -2

export const AddressBox = () => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  const {
    getItemsGroupedByPolicy,
    items,
    setSelectedAddress,
    selectedAddressId,
    customAddress,
    setCustomAddress,
    isPrivateAddress,
    setIsPrivateAddress,
  } = useCart()

  const { data } = useQuery(queries.policyList(), ecomPolicyResources.getList)

  const { data: policyData } = useQuery(
    queries.policy(Number(Object.keys(getItemsGroupedByPolicy())[0])),
    () => ecomPolicyResources.get(Number(Object.keys(getItemsGroupedByPolicy())[0])),
    {
      enabled: items.length > 0,
    }
  )

  const getIsCustomAddressAvailable = useMemo(() => {
    const groupedPolicies = getItemsGroupedByPolicy()
    const policyIds = Object.keys(groupedPolicies)
    if (policyIds.length === 0) return false

    return policyIds.every(policyId => {
      const policy = data?.policyList.find(p => p.id === Number(policyId))
      return policy?.isCustomAddressActivated
    })
  }, [data?.policyList, getItemsGroupedByPolicy])

  const addresses = useMemo(() => {
    if (!policyData?.addresses) return []
    let addresses = []

    if (getIsCustomAddressAvailable) {
      addresses.push({
        label: translate('Custom address'),
        value: CUSTOM_ADDRESS_ID,
      })
    }

    addresses = [
      ...addresses,
      ...policyData.addresses.map(address => ({
        label: `${address.address}, ${address.zip} ${address.city}`,
        value: address.id,
      })),
    ]

    return addresses
  }, [policyData, translate, getIsCustomAddressAvailable])

  const handleSelectAddress = (option: ValueLabelPair | null) => {
    if (!option) throw Error('No option selected!')
    if (typeof option.value === 'string') throw Error('Incorrect address id!')
    setSelectedAddress(option.value)
    if (option.value === CUSTOM_ADDRESS_ID) {
      setIsPrivateAddress(true)
      return
    }
    setIsPrivateAddress(false)
  }

  return (
    <div className="flex flex-col gap-2 pt-2 sm:my-6 sm:rounded-lg sm:border sm:border-solid sm:border-gray-300 sm:p-2 md:gap-4 md:p-3">
      <StyledText
        size="regular"
        colorMode="gray"
        fontWeight="bold"
        className="uppercase tracking-wider"
      >
        <Translation id="Delivery address" />
      </StyledText>

      <Select
        id="delivery-address"
        placeholder={translate('Select delivery address')}
        className="react-select max-w-full"
        options={addresses}
        onChange={option => handleSelectAddress(option)}
        value={selectedAddressId}
      />

      {selectedAddressId === CUSTOM_ADDRESS_ID && (
        <div className="max-mobile:flex-col mobile:flex-wrap flex gap-1 overflow-hidden transition sm:gap-2">
          <div className="mobile:basis-full">
            <Input
              id="address"
              className="m-0 w-full py-2 text-base md:py-3"
              label={<Translation id="Address" />}
              value={customAddress?.address || ''}
              onChange={e =>
                setCustomAddress(
                  e.target.value,
                  customAddress?.zip || '',
                  customAddress?.city || ''
                )
              }
            />
          </div>

          <div className="mobile:basis-1/3">
            <Input
              id="zip"
              className="m-0 w-full py-2 text-base md:py-3"
              label={<Translation id="Zip" />}
              value={customAddress?.zip || ''}
              maxLength={10}
              onChange={e => {
                setCustomAddress(
                  customAddress?.address || '',
                  e.target.value,
                  customAddress?.city || ''
                )
              }}
            />
          </div>

          <div className="mobile:grow">
            <InputWithValidation
              id="city"
              className="m-0 w-full py-2 text-base md:py-3"
              errorMessage={<Translation id="City name may only include letters" />}
              validator={isOnlyLetters}
              label={<Translation id="City" />}
              value={customAddress?.city || ''}
              onChange={e =>
                setCustomAddress(
                  customAddress?.address || '',
                  customAddress?.zip || '',
                  e.target.value
                )
              }
            />
          </div>
        </div>
      )}

      {getIsCustomAddressAvailable && selectedAddressId === CUSTOM_ADDRESS_ID && (
        <RadioGroup id="delivery-address-type" className="max-sm:mb-2">
          <div className="mobile:flex-row flex w-full flex-col justify-between gap-2 sm:gap-4">
            <Radio
              id="is-business-address"
              className={twJoin(
                'flex-1 rounded-lg border border-solid px-4 py-2',
                isPrivateAddress === undefined
                  ? 'border-gray-300'
                  : !isPrivateAddress
                    ? radioBtnStyleByWhiteLabel[whiteLabel] || radioBtnStyleByWhiteLabel.skytech
                    : 'border-gray-300'
              )}
              checked={isPrivateAddress === false}
              onChange={() => {
                setIsPrivateAddress(false)
              }}
              onClick={() => {
                setIsPrivateAddress(false)
              }}
              label={
                <StyledText
                  colorMode={
                    isPrivateAddress === undefined
                      ? 'black'
                      : !isPrivateAddress
                        ? undefined
                        : 'black'
                  }
                  whiteLabel={whiteLabel}
                  className={twJoin(
                    'flex items-center gap-2 !font-bold',
                    isPrivateAddress === false && whiteLabel === 'elkjop' && '!text-elkjop',
                    isPrivateAddress === false && whiteLabel === 'ice' && '!text-black'
                  )}
                >
                  <OfficeBuildingOutlineIcon />
                  <Translation id="Business address" />
                </StyledText>
              }
            />

            <Radio
              id="is-private-address"
              className={twJoin(
                'flex-1 rounded-lg border border-solid px-4 py-2',
                isPrivateAddress === undefined
                  ? 'border-gray-300'
                  : isPrivateAddress
                    ? radioBtnStyleByWhiteLabel[whiteLabel] || radioBtnStyleByWhiteLabel.skytech
                    : 'border-gray-300'
              )}
              checked={isPrivateAddress}
              onChange={() => {
                setIsPrivateAddress(true)
              }}
              onClick={() => {
                setIsPrivateAddress(true)
              }}
              label={
                <StyledText
                  colorMode={
                    isPrivateAddress === undefined
                      ? 'black'
                      : isPrivateAddress
                        ? undefined
                        : 'black'
                  }
                  whiteLabel={whiteLabel}
                  className={twJoin(
                    'flex items-center gap-2 !font-bold',
                    isPrivateAddress && whiteLabel === 'elkjop' && '!text-elkjop',
                    isPrivateAddress && whiteLabel === 'ice' && '!text-black'
                  )}
                >
                  <HomeOutlineIcon />
                  <Translation id="Private address" />
                </StyledText>
              }
            />
          </div>
        </RadioGroup>
      )}
    </div>
  )
}
